import type { IAnalyticalBackend } from '@gooddata/sdk-backend-spi';
import { useBackendStrict, useWorkspaceStrict } from '@gooddata/sdk-ui';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import {
    ExportReportDefinition,
    ExportScheduleParameters,
    QueueReportExportResult,
    useRepositoryMutation,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { DateTimeUtils, lt, showSuccessAlert, useLocalizeText } from '@mews/framework';
import { StructuredDate } from '@optimus-web/core';
import { CustomDateRange, DatePeriod } from '../../../common/components/ReportingDateRangePicker';
import { DateRange, FilterFieldsValues, FilterValue, ReportState, ReportType } from '../../../common/types';
import { ExportFieldsValues, ExportParametersState, GDReportConfig } from '../../types';
import { useGoodDataParameters } from '../hooks';
import { getExportExecution } from './utils';

interface MutationProps {
    onSuccess?: (response: QueueReportExportResult) => void;
}

export const useExportMutation = ({ onSuccess }: MutationProps) =>
    useRepositoryMutation('reportExporter', 'queueExport', {
        onSuccess,
    });

export const useCreateExportScheduleMutation = ({ onSuccess }: MutationProps) =>
    useRepositoryMutation('exportSchedule', 'create', {
        onSuccess,
    });

export const useUpdateExportScheduleMutation = ({ onSuccess }: MutationProps) =>
    useRepositoryMutation('exportSchedule', 'update', {
        onSuccess,
    });

export const useGetExportTargetsQuery = () => useRepositoryQuery('targets', 'getAll', {});

export interface HandleExportProps {
    workspaceId?: string;
}

export interface HandleExportScheduleProps {
    workspaceId?: string;
}

const customDateRangeToDateRange = (customDateRange: CustomDateRange) => {
    const dateRange: DateRange = {
        datePeriod: DatePeriod.Custom,
        endDate: DateTimeUtils.structuredDateToISOString(customDateRange.endDate as StructuredDate),
        startDate: DateTimeUtils.structuredDateToISOString(customDateRange.startDate as StructuredDate),
    };
    return dateRange;
};

interface CreateReportConfig {
    backend: IAnalyticalBackend;
    workspace: string;
    selectedConfigs: Record<string, GDReportConfig>;
    sectionsValues: Record<string, ExportFieldsValues>;
    localizeText: ReturnType<typeof useLocalizeText>;
}

function createReports(config: CreateReportConfig) {
    const { backend, workspace, selectedConfigs, sectionsValues, localizeText } = config;
    return Object.entries(selectedConfigs)
        .map(([key, reportSection]) => {
            const { dateType, filters, rawDataExportConfig } = reportSection;
            if (!rawDataExportConfig) {
                return null;
            }
            const resolvedRawDataExportConfig =
                typeof rawDataExportConfig === 'function'
                    ? rawDataExportConfig({
                          activeConfigId: key,
                          dateRange: sectionsValues[key].dateRange,
                          filterValues: sectionsValues[key].filterValues,
                          groupBy: sectionsValues[key].groupBy,
                          viewMode: sectionsValues[key].viewMode,
                      } as ReportState)
                    : rawDataExportConfig;
            const { attributes, facts, metrics } = resolvedRawDataExportConfig;
            const localizedAttributes = attributes
                ? attributes.map(({ id, title }) => ({
                      rawName: `a_${id}`,
                      localizedValue: localizeText({ textKey: title }),
                  }))
                : [];
            const localizedFacts = facts
                ? facts.map(({ id, title }) => ({
                      rawName: `m_${id}`,
                      localizedValue: localizeText({ textKey: title }),
                  }))
                : [];
            const localizedMetrics = metrics
                ? metrics.map(({ id, title }) => ({
                      rawName: `m_${id}`,
                      localizedValue: localizeText({ textKey: title }),
                  }))
                : [];

            const customDateRange = (sectionsValues[key].dateRange ||
                reportSection.defaultDateRange) as CustomDateRange;
            const dateRange =
                customDateRange.startDate && customDateRange.endDate
                    ? customDateRangeToDateRange(customDateRange)
                    : (customDateRange as DateRange);

            const realFilterValues = sectionsValues[key].filterValues as FilterFieldsValues;

            const execution = getExportExecution({
                backend,
                workspace,
                attributes,
                dateRange,
                dateType,
                facts,
                filters,
                filterValues: realFilterValues,
                metrics,
            });

            const report: ExportReportDefinition = {
                identifier: reportSection.title,
                requestDefinition: JSON.stringify(execution),
                filters: (filters ?? [])
                    .filter(({ filterId }) => {
                        const values = (sectionsValues[key].filterValues as Record<string, FilterValue>)[filterId];
                        return Array.isArray(values) || typeof values === 'string' ? values.length > 0 : false;
                    })
                    .map(({ filterId, label, type }) => ({
                        name: label,
                        type,
                        value: String((sectionsValues[key].filterValues as Record<string, FilterValue>)[filterId]),
                    })),
                headers: [...localizedAttributes, ...localizedFacts, ...localizedMetrics],
            };
            return report;
        })
        .filter(Boolean) as ExportReportDefinition[];
}

export const useHandleExport = ({ workspaceId }: HandleExportProps) => {
    const localizeText = useLocalizeText();
    const { data } = useGoodDataParameters();
    const enterpriseId = useCurrentEnterpriseId();

    const exportMutation = useExportMutation({
        onSuccess: () => {
            showSuccessAlert({
                description: lt('ExportQueuedSuccessMessage'),
            });
        },
    });

    const backend = useBackendStrict(undefined, 'useHandleExport');
    const workspace = useWorkspaceStrict(workspaceId || data?.workspaceId, 'useHandleExport');

    if (!backend || !workspace) return null;

    return ({ fileType, sectionsValues, selectedConfigs }: ExportParametersState) =>
        exportMutation.mutate({
            enterpriseId,
            fileType,
            reports: createReports({ backend, workspace, selectedConfigs, sectionsValues, localizeText }),
            reportType: Object.values(selectedConfigs)[0].reportType as ReportType,
            workspaceId: String(workspace),
        });
};

export const useHandleExportSchedule = ({ workspaceId }: HandleExportScheduleProps) => {
    const localizeText = useLocalizeText();
    const { data } = useGoodDataParameters();
    const enterpriseId = useCurrentEnterpriseId();

    const exportScheduleMutation = useCreateExportScheduleMutation({
        onSuccess: () => {
            showSuccessAlert({
                description: lt('ExportQueuedSuccessMessage'),
            });
        },
    });

    const backend = useBackendStrict(undefined, 'useHandleExport');
    const workspace = useWorkspaceStrict(workspaceId || data?.workspaceId, 'useHandleExport');

    if (!backend || !workspace) return null;

    return (
        { fileType, sectionsValues, selectedConfigs }: ExportParametersState,
        exportScheduleParameters: ExportScheduleParameters
    ) =>
        exportScheduleMutation.mutate({
            enterpriseId,
            exportScheduleParameters,
            reportExportParameters: {
                fileType,
                notifyCreatorAboutExport: exportScheduleParameters.notifyCreatorAboutExport,
                reports: createReports({ backend, workspace, selectedConfigs, sectionsValues, localizeText }),
                reportType: Object.values(selectedConfigs)[0].reportType as ReportType,
                workspaceId: String(workspace),
            },
        });
};
